import classNames from 'classnames/bind';
import { gql } from '@apollo/client';
import { Container, Button } from '@/components';
import styles from './Subnav.module.scss';
import { flatListToHierarchical } from '@faustwp/core';
import { useRouter } from 'next/compat/router'
import Link from 'next/link';
import React, { useContext, useState, useEffect, useRef } from 'react';
import checkForExternalLink from '@/helpers/checkForExternalLink';
import { NetspendContext } from 'library/contexts/NetspendContext';

let cx = classNames.bind(styles);

export function Subnav({ menuItems, navigationSettings }) {
	const subNav = useRef(null);
	const currentPathname = useRouter().asPath.split(/[?#]/)[0];
	const [isSticky, setIsSticky] = useState(false);
	const hierarchicalMenuItems = flatListToHierarchical(menuItems);
	const { stripeHeight } = useContext(NetspendContext);
	const ctaOverride = navigationSettings?.overrideHeaderCta;
	let subnavHeadingElem, subnavHeadingLabel;

	for (const item of hierarchicalMenuItems) {
		const { id, label, subnavMenuItemSettings } = item;
		if (subnavMenuItemSettings.isMenuHeading) {
			subnavHeadingElem = (
				<span id={`nav-heading-${id}`} className={cx(['ns_nav-heading', 'nav-heading'])}>
					{label ?? ''}
				</span>
			);
			subnavHeadingLabel = label;
			break;
		}
	}

	function InfoChip({ text }) {
		return <span className={cx(['ns_info_chip', 'info-chip'])}>{text}</span>;
	}

	function Menu({ items }) {
		return (
			<>
				{subnavHeadingElem}
				<ul className={cx(['ns_menu-ul', 'menu__ul'])}>
					{items.map((item, index) => {
						const { id, path, label, children, cssClasses, subnavMenuItemSettings, navItemSettings, target, connectedNode } = item;
						const elem = connectedNode ? Link : 'a';
						const isCurrentPage = `${currentPathname}` === path;

						// @TODO - Remove guard clause after ghost menu items are no longer appended to array.
						if (!item.hasOwnProperty('__typename')) {
							return null;
						}
						if (!subnavMenuItemSettings.isCta && !subnavMenuItemSettings.isMenuHeading) {
							return (
								<li key={id} className={cx(['ns_menu-li', 'menu__li', cssClasses, isCurrentPage && 'current-page'])}>
									{path ? (
										React.createElement(
											elem,
											{
												href: path,
												className: cx(['menu__a', children.length && 'parentCategory']),
												target: target,
											},
											label ?? ''
										)
									) : (
										<span className={cx(['menu__span', children.length && 'parentCategory'])}>{label ?? ''}</span>
									)}
									{navItemSettings.infoChip && <InfoChip text={navItemSettings.infoChip} />}
									{children.length ? <Menu items={children} /> : null}
								</li>
							);
						}
					})}
				</ul>
			</>
		);
	}

	function Ctas({ items }) {
		let ctaCount = 0;
		return (
			<ul className={cx('ns_menu-ul', 'menu__ul', 'menu__ul-ctas')}>
				{items.map((item) => {
					const { id, path, label, cssClasses, subnavMenuItemSettings, navItemSettings, target, connectedNode } = item;
					const elem = connectedNode ? Link : 'a';

					// @TODO - Remove guard clause after ghost menu items are no longer appended to array.
					if (!item.hasOwnProperty('__typename')) {
						return null;
					}
					if (subnavMenuItemSettings.isCta && !subnavMenuItemSettings.isMenuHeading) {
						ctaCount++;
						const hasCtaOverride = ctaOverride && ctaCount === 1 ? true : false;
						return (
							<li key={id} className={cx(['menu__li', 'cta', cssClasses])}>
								{subnavMenuItemSettings.style === 'link' ? (
									React.createElement(
										hasCtaOverride ? (checkForExternalLink(ctaOverride.url) ? 'a' : Link) : elem,
										{
											href: hasCtaOverride ? ctaOverride.url : path ?? '',
											className: cx('cta-link'),
											target: hasCtaOverride ? ctaOverride.target : target,
										},
										[hasCtaOverride && ctaOverride.title ? ctaOverride.title : label ?? '', navItemSettings.infoChip && <InfoChip text={navItemSettings.infoChip} />]
									)
								) : (
									<Button href={hasCtaOverride ? ctaOverride.url : path ?? ''} className={cx(subnavMenuItemSettings.style)} target={hasCtaOverride ? ctaOverride.target : target} wordressPageInNav={connectedNode ? true : false}>
										{hasCtaOverride && ctaOverride.title ? ctaOverride.title : label ?? ''}
										{navItemSettings.infoChip && <InfoChip text={navItemSettings.infoChip} />}
									</Button>
								)}
							</li>
						);
					}
				})}
				{ctaCount === 0 && ctaOverride ? (
					<li className={cx(['menu__li', 'cta'])}>
						<Button href={ctaOverride.url} className={cx('filled-button')} target={ctaOverride.target ? ctaOverride.target : null}>
							{ctaOverride.title}
						</Button>
					</li>
				) : null}
			</ul>
		);
	}

	useEffect(() => {
		function handleWindowScroll() {
      const position = subNav.current.getBoundingClientRect();
			if (position.top === 0) {
				setIsSticky(true);
			} else {
				setIsSticky(false);
			}
		}
		handleWindowScroll();
		window.addEventListener('scroll', handleWindowScroll);
		return () => {
			window.removeEventListener('scroll', handleWindowScroll);
		};
	}, [stripeHeight]);

	return (
		<div className={cx(['ns_subnav-container', 'subnav-container', 'bg-black', isSticky ? 'sticky' : null])} style={stripeHeight && { '--stripeHeight': `${stripeHeight}px` }} ref={subNav}>
			<Container className={cx('header-container')}>
				<nav id="subnav" className={cx(['subnav'])} role="navigation" aria-label={subnavHeadingLabel ? `${subnavHeadingLabel} menu` : `${menuItems[0]?.menu?.node?.name} menu`}>
					<Menu items={hierarchicalMenuItems} />
					<Ctas items={hierarchicalMenuItems} />
				</nav>
			</Container>
		</div>
	);
}

Subnav.fragments = {
	entry: gql`
		fragment SubnavMenuItemFragment on MenuItem {
			id
			path
			label
			parentId
			cssClasses
			menu {
				node {
					name
				}
			}
			subnavMenuItemSettings {
				isCta
				isMenuHeading
				style
			}
			navItemSettings {
				infoChip
			}
			connectedNode {
				node {
					id
				}
			}
			target
		}
	`,
};
